import { CheckIcon, MinusIcon } from '@phosphor-icons/react'
import React, { useEffect, useRef } from 'react'

import { Icon } from '@/components/ui/icon'

import { CHECKBOX_ICON_SIZE_MAP } from './checkbox.constants'
import * as S from './checkbox.styled'
import { CheckboxSize } from './checkbox.types'

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  checked?: boolean
  /** Tri-state "some but not all descendants selected" - visually a dash, not a check. */
  indeterminate?: boolean
  label?: string
  size?: CheckboxSize
}

export const Checkbox = ({
  checked = false,
  indeterminate = false,
  label,
  disabled = false,
  size = 'medium',
  onChange,
  ...props
}: CheckboxProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  // Note: `indeterminate` has no HTML attribute equivalent - it's a DOM-only IDL
  // property, so it has to be set imperatively on the underlying <input>.
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  let checkboxIcon: React.ReactNode = null
  if (indeterminate) {
    checkboxIcon = <Icon icon={MinusIcon} size={CHECKBOX_ICON_SIZE_MAP[size]} color="white" />
  } else if (checked) {
    checkboxIcon = <Icon icon={CheckIcon} size={CHECKBOX_ICON_SIZE_MAP[size]} color="white" />
  }

  return (
    <S.CheckboxLabel $disabled={disabled}>
      <S.CheckboxInput
        ref={inputRef}
        aria-checked={indeterminate ? 'mixed' : checked}
        checked={checked}
        disabled={disabled}
        type="checkbox"
        onChange={onChange}
        {...props}
      />
      <S.CheckboxBox $checked={checked || indeterminate} $disabled={disabled} $size={size}>
        {checkboxIcon}
      </S.CheckboxBox>
      {label && (
        <S.CheckboxLabelText $disabled={disabled} $size={size}>
          {label}
        </S.CheckboxLabelText>
      )}
    </S.CheckboxLabel>
  )
}
